//
// # EditExprLabelDialog
//
// Modal dialog for editing an existing expression label.
// Pre-populated from the current label's data.
// Fields: Kind, plus kind-specific fields (port name, parameter name).
//

import React, { useState, useCallback } from "react";
import { Modal } from "../Modal/Modal";
import { schema } from "GenAlphaSchema";
import {
  DEFAULT_PARAM_NAME,
  DEFAULT_PORT_NAME,
  EXPR_KIND_OPTIONS,
  type ExprLabelConfig,
} from "./AddExprLabelDialog";

// Narrow a raw `<select>` value to a `schema.symbol.ExprKind` by listing
// every kind explicitly — this lets TS narrow without a type assertion,
// and keeps the rule against runtime `x is T` type guards happy.
function asExprKind(v: string): schema.symbol.ExprKind | null {
  switch (v) {
    case "cell-name":
    case "inst-name":
    case "port-name":
    case "port-debug":
    case "inst-param":
    case "invalid":
      return v;
    default:
      return null;
  }
}

// Current data shown when the dialog opens. The name/paramName fields
// are only meaningful for the matching kinds — provide sensible defaults
// for the others so switching kinds inside the dialog still works.
export interface EditExprLabelCurrentData {
  readonly kind: schema.symbol.ExprKind;
  readonly name?: string;
  readonly paramName?: string;
}

interface EditExprLabelDialogProps {
  readonly currentData: EditExprLabelCurrentData;
  readonly onConfirm: (config: ExprLabelConfig) => void;
  readonly onCancel: () => void;
}

export function EditExprLabelDialog({
  currentData,
  onConfirm,
  onCancel,
}: EditExprLabelDialogProps) {
  const [kind, setKind] = useState<schema.symbol.ExprKind>(currentData.kind);
  const [name, setName] = useState(currentData.name ?? DEFAULT_PORT_NAME);
  const [paramName, setParamName] = useState(
    currentData.paramName ?? DEFAULT_PARAM_NAME
  );

  // Only port-name and inst-param have a free-text field that needs validation;
  // other kinds derive their text from the model and have nothing to validate.
  const trimmedName = name.trim();
  const trimmedParamName = paramName.trim();
  const canSubmit =
    kind === "port-name"
      ? trimmedName.length > 0
      : kind === "inst-param"
      ? trimmedParamName.length > 0
      : true;

  const handleConfirm = useCallback(() => {
    if (!canSubmit) return;
    const config: ExprLabelConfig = { kind };
    if (kind === "port-name") {
      onConfirm({ ...config, name: trimmedName });
    } else if (kind === "inst-param") {
      onConfirm({ ...config, paramName: trimmedParamName });
    } else {
      onConfirm(config);
    }
  }, [canSubmit, kind, trimmedName, trimmedParamName, onConfirm]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && canSubmit) {
        e.preventDefault();
        handleConfirm();
      }
    },
    [canSubmit, handleConfirm]
  );

  const selectStyle = {
    backgroundColor: "var(--color-ide-surface-2)",
    borderColor: "var(--color-ide-border)",
    color: "var(--color-ide-text)",
  };

  const inputStyle = (valid: boolean) => ({
    backgroundColor: "var(--color-ide-surface-2)",
    borderColor: valid ? "var(--color-ide-border)" : "var(--color-ide-error)",
    color: "var(--color-ide-text)",
  });

  return (
    <Modal open={true} onClose={onCancel} title="Edit Expression Label">
      <div onKeyDown={handleKeyDown} className="flex flex-col gap-3">
        {/* Kind */}
        <div className="flex flex-col gap-1">
          <label
            className="text-[12px]"
            style={{ color: "var(--color-ide-text-muted)" }}
          >
            Kind
          </label>
          <select
            value={kind}
            onChange={(e) => {
              const k = asExprKind(e.target.value);
              if (k !== null) setKind(k);
            }}
            autoFocus
            className="px-2 py-1.5 text-[13px] rounded border outline-none"
            style={selectStyle}
          >
            {EXPR_KIND_OPTIONS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </div>

        {/* Port Name (only for port-name kind) */}
        {kind === "port-name" && (
          <div className="flex flex-col gap-1">
            <label
              className="text-[12px]"
              style={{ color: "var(--color-ide-text-muted)" }}
            >
              Port Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              className="px-2 py-1.5 text-[13px] rounded border outline-none"
              style={inputStyle(trimmedName.length > 0)}
            />
          </div>
        )}

        {/* Param Name (only for inst-param kind) */}
        {kind === "inst-param" && (
          <div className="flex flex-col gap-1">
            <label
              className="text-[12px]"
              style={{ color: "var(--color-ide-text-muted)" }}
            >
              Parameter Name
            </label>
            <input
              type="text"
              value={paramName}
              onChange={(e) => {
                setParamName(e.target.value);
              }}
              className="px-2 py-1.5 text-[13px] rounded border outline-none"
              style={inputStyle(trimmedParamName.length > 0)}
            />
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 text-[13px] rounded border cursor-pointer bg-transparent"
            style={{
              borderColor: "var(--color-ide-border)",
              color: "var(--color-ide-text)",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canSubmit}
            className="px-4 py-1.5 text-[13px] rounded border-none"
            style={{
              backgroundColor: canSubmit
                ? "var(--color-ide-accent)"
                : "var(--color-ide-surface-2)",
              color: canSubmit ? "#ffffff" : "var(--color-ide-text-dim)",
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  );
}
